import {
  FormattedPlanDetails,
  OpenMessageType,
  SubscriptionDuration,
  SubscriptionPaymentMethodType,
  UserProperties,
} from "@shared/types";

export enum MessageType {
  WEBVIEW_READY = "webview_ready",
  BIOMETRIC_LOGIN_REQUEST = "biometric_login_request",
  INITIATE_PAYMENT_REQUEST = "initiate_payment_request",
  CLEAR_BADGE_COUNT = "clear_badge_count",
  SHARE_REQUEST = "share_request",
  CONSOLE = "console",
  TRIGGER_SAVE_BIOMETRICS = "trigger_save_biometrics",
  CLEAR_BIOMETRICS = "clear_biometrics",
  GET_BIOMETRIC_DATA = "biometric_data_request",
  TOGGLE_USER_BIOMETRIC = "toggle_user_biometric",
  USER_UPDATED = "user_updated",
  OPEN_AUTH_SESSION = "open_auth_session",
  LEAVE_REVIEW = "leave_review",
  REQUEST_INTERCOM_ACTION = "request_intercom_action",
  REQUEST_CLIPBOARD_DATA = "request_clipboard_data",
  COPY_TO_CLIPBOARD = "copy_to_clipboard",
  THEME_UPDATED = "theme_updated",
  NAVIGATE_BACK_REQUEST = "navigate_back_request",
  NAVIGATION_REQUEST = "navigation_request",
  NOTIFICATION_NAVIGATION_REQUEST = "notification_navigation_request",
  KEYBOARD_STATUS_CHANGED = "keyboard_status_changed",
  OPEN_MESSAGE = "open_message",
  RESPONSE = "response",
  SUBSCRIPTION_APPROVED = "subscription_approved",
}

export interface LoginCredentials {
  email?: string;
  password?: string;
  remember_me?: boolean;
}

export interface BaseUserBiometricsData {
  biometricsEnabled?: boolean;
  hasSavedBiometrics?: boolean;
}

export interface UserBiometricsData extends BaseUserBiometricsData {
  biometricsSupported?: boolean;
}

export type StorageBiometricsData = { [email: string]: BaseUserBiometricsData };

export interface MobileLoginData {
  credentials?: LoginCredentials;
  device_data: {
    mobile_identifier?: string;
    unique_identifier?: string;
  };
}

export type PaymentDetails = {
  plan: FormattedPlanDetails;
  duration: SubscriptionDuration;
  promoCode?: string;
  discount?: number;
  userUuid: string;
  productId: string;
  paymentMethod: SubscriptionPaymentMethodType;
  isUpgrade?: boolean;
  isCurrentPlan?: boolean;
};

export interface PaymentResult {
  transactionId: string;
  status: "completed" | "failed" | "pending";
  error?: string;
  isUserCancelled?: boolean;
  isAlreadySubscribed?: boolean;
}

export interface ShareContent {
  url?: string;
  title?: string;
}

export interface ShareResult {
  shared: boolean;
  activityType?: string;
}

export interface CanGoBackResponse {
  canGoBack: boolean;
}

export interface NavigationRequest {
  pathname: string;
}

export interface MessageDetails {
  text: string;
  type: OpenMessageType;
  duration?: number;
}

export enum IntercomActionType {
  UPDATE,
  SHOW_SPACE,
  SHOW_ARTICLE,
  SHOW,
  SET_LAUNCHER_VISIBILITY,
}

export interface IntercomAction {
  type: IntercomActionType;
  itemId?: string;
  isLauncherVisible?: boolean;
}

export interface ThemeUpdatedData {
  isDarkMode: boolean;
}

export interface ClipboardData {
  clipboardText?: string;
}

export type MessagePayload =
  | LoginCredentials
  | PaymentDetails
  | PaymentResult
  | ShareContent
  | ShareResult
  | CanGoBackResponse
  | NavigationRequest
  | UserProperties
  | MessageDetails
  | IntercomAction
  | ThemeUpdatedData
  | ClipboardData
  | { [key: string]: any };

export enum MessageStatus {
  SUCCESS = "success",
  ERROR = "error",
}

export enum KeyboardStatus {
  DID_HIDE = "did_hide",
  DID_SHOW = "did_show",
  WILL_SHOW = "will_show",
}

export interface KeyboardStatusChangedEvent {
  keyboardStatus: KeyboardStatus;
}

export interface BridgeMessage<T = MessagePayload> {
  id: string;
  type: MessageType;
  payload?: T;
  status?: MessageStatus;
  error?: string;
}

export interface PromiseCallbacks<T = any> {
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: any) => void;
}
